import { Divider, Table } from 'antd'
import { SortOrder } from 'antd/lib/table'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { LoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors'
import PerText from '../../../components/custom_components/Text'
import useCompleteNationalContractEmployeeLevelHistory from '../../../graphql/contractHistory/hooks/useCompleteNationalContractEmployeeLevelHistory'
import { useDateTime } from '../../../shared/useDateTime'
import { NationalContractEmployeeLevelDto } from '../../../types'
import { TimeZoneAndLocale } from '../../../types.custom'

interface Props {
  employeeId: string
  noDivider?: boolean
  resigned?: boolean
  timezone: TimeZoneAndLocale
}

export const NationalContractLevelHistoryTable: FC<Props> = ({ employeeId, noDivider = false, timezone, resigned }) => {
  const { formatDateShort } = useDateTime(timezone)
  const { t } = useTranslation()

  // load Data
  const { data, loading, error } = useCompleteNationalContractEmployeeLevelHistory(employeeId, resigned)

  if (loading || error || !data) {
    return <LoadingAndErrors isLoading={loading} hasNoData={!data} error={error} />
  }

  const levelHistory: NationalContractEmployeeLevelDto[] =
    data.nationalContractEmployeeLevelsByEmployeeId.nationalContractEmployeeLevelHistory
  const showLevelHistory = !!levelHistory && levelHistory.length > 0

  const columns = [
    {
      key: 'nationalContractLevel-level',
      title: t('profile.contract.nationalContractLevel.level'),
      className: 'textAlign-left minWidth-column-xl',
      sorter: (levelA, levelB) => {
        const a = levelA.level ? levelA.level : ' '
        const b = levelB.level ? levelB.level : ' '
        return a.localeCompare(b)
      },
      render: (tableData: NationalContractEmployeeLevelDto) =>
        tableData.type &&
        tableData.level &&
        `${t(`employee.nationalContract.shortType.${tableData.type}`)} - ${t(`employee.nationalContract.level.${tableData.level}`)}`,
    },
    {
      key: 'nationalContractLevel-fromDate',
      title: t('profile.contract.nationalContractLevel.fromDate'),
      className: 'textAlign-left minWidth-column-s',
      defaultSortOrder: 'descend' as SortOrder,
      sorter: (dateA: NationalContractEmployeeLevelDto, dateB: NationalContractEmployeeLevelDto) => {
        const a = dateA.fromDate ? dateA.fromDate : ' '
        const b = dateB.fromDate ? dateB.fromDate : ' '
        return a.localeCompare(b)
      },
      render: (tableData: NationalContractEmployeeLevelDto) => tableData.fromDate && formatDateShort(tableData.fromDate),
    },
    {
      key: 'nationalContractLevel-toDate',
      title: t('profile.contract.nationalContractLevel.toDate'),
      className: 'textAlign-left minWidth-column-s',
      sorter: (dateA, dateB) => {
        const a = dateA.toDate ? dateA.toDate : ' '
        const b = dateB.toDate ? dateB.toDate : ' '
        return a.localeCompare(b)
      },
      render: (tableData: NationalContractEmployeeLevelDto) => tableData.toDate && formatDateShort(tableData.toDate),
    },
    {
      key: 'nationalContractLevel-note',
      className: 'minWidth-column-xl',
      title: t('profile.contract.nationalContractLevel.note'),
      render: (row: NationalContractEmployeeLevelDto) => row.note && <PerText>{row.note}</PerText>,
    },
  ]

  return (
    <>
      {showLevelHistory && (
        <>
          {!noDivider && <Divider />}
          <PerText appearance="tile-heading" mb="s">
            {t('profile.contract.nationalContractLevel.header')}
          </PerText>
          <Table
            dataSource={levelHistory.map((nationalContractLevel) => ({
              ...nationalContractLevel,
              key: nationalContractLevel.id,
            }))}
            className="small"
            columns={columns}
            pagination={false}
          />
        </>
      )}
    </>
  )
}
